import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE, SITE_URL } from './siteConfig';

function toCanonicalUrl(pathname) {
  if (!pathname) return SITE_URL;
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${SITE_URL}${cleanPath}`;
}

function Seo({
  title,
  description,
  keywords,
  canonicalPath,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  noindex = false,
  robots,
  structuredData = [],
  preloadImages = [],
}) {
  const location = useLocation();
  const resolvedCanonical = canonicalUrl || toCanonicalUrl(canonicalPath || location.pathname);
  const resolvedRobots = robots || (noindex ? 'noindex,nofollow' : 'index,follow');
  const resolvedOgTitle = ogTitle || title;
  const resolvedOgDescription = ogDescription || description;
  const resolvedTwitterTitle = twitterTitle || title;
  const resolvedTwitterDescription = twitterDescription || description;
  const jsonLdItems = Array.isArray(structuredData) ? structuredData : [structuredData];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={resolvedRobots} />
      <link rel="canonical" href={resolvedCanonical} />

      {preloadImages.map((img, index) => (
        <link
          key={index}
          rel="preload"
          as="image"
          href={img.href}
          type={img.type}
          {...(img.fetchPriority ? { fetchPriority: img.fetchPriority } : {})}
        />
      ))}

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTwitterTitle} />
      <meta name="twitter:description" content={resolvedTwitterDescription} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdItems
        .filter(Boolean)
        .map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
}

export default Seo;
